/**
 * The AI Service hub's title, and the one control that renames it (task 784).
 *
 * The heading resolves through `endpointDisplayName` — `name → description →
 * method + path` — so a service nobody has named reads exactly as it did before
 * the field existed. What this adds is the ability to give it one.
 *
 * **Independent of the page's edit mode, deliberately.** Naming a thing is not
 * the same act as reconfiguring it: a User who wants to fix a title should not
 * have to enter (and then remember to leave) a mode that also exposes the
 * request format and the queued toggle. So this is the Assistant history
 * rename's pattern (task 777) rather than a field inside the Overview form —
 * pencil, Enter commits, Escape cancels, blur abandons.
 *
 * **An empty value never commits.** Clearing the box and pressing Enter closes
 * the editor untouched rather than blanking the title, because a nameless
 * service would silently fall back to its description and look like the rename
 * had done something arbitrary. Removing a name is not a gesture this control
 * offers; changing it is.
 */
import { useState } from 'react';
import { PencilIcon } from '@heroicons/react/24/outline';
import type { Endpoint } from '@/types';
import { useUpdateEndpoint } from '@/hooks/useEndpoints';
import { useToast } from '@/contexts/ToastContext';
import { endpointDisplayName } from '@/utils/endpointLabel';
import { describeEndpointSaveError } from '@/components/endpoints/endpointSaveError';

interface ServiceTitleEditorProps {
  endpoint: Endpoint;
}

export function ServiceTitleEditor({ endpoint }: ServiceTitleEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const updateEndpoint = useUpdateEndpoint();
  const toast = useToast();

  const title = endpointDisplayName(endpoint);

  const commit = async (raw: string) => {
    const name = raw.trim();
    setIsEditing(false);
    // Nothing to do for an empty box or an unchanged name — neither is a rename,
    // and firing a PATCH for either would be a write with no meaning.
    if (!name || name === (endpoint.name ?? '')) return;

    try {
      await updateEndpoint.mutateAsync({ id: endpoint.id, data: { name } });
    } catch (error) {
      toast.error(describeEndpointSaveError(error, 'Failed to rename this AI Service').message);
    }
  };

  if (isEditing) {
    return (
      <input
        type="text"
        autoFocus
        data-testid="service-title-input"
        aria-label="AI Service name"
        // The box opens on the stored NAME, not on the displayed title: a
        // service showing its description as a fallback starts from empty, so
        // the first keystroke writes a name rather than editing a copy of the
        // description that was never in this field.
        defaultValue={endpoint.name ?? ''}
        placeholder="Name this AI Service"
        onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void commit(event.currentTarget.value);
          } else if (event.key === 'Escape') {
            setIsEditing(false);
          }
        }}
        onBlur={() => setIsEditing(false)}
        className="min-w-0 flex-1 rounded-md border border-indigo-400 bg-white px-2 py-1 text-2xl font-semibold leading-7 text-gray-900 focus:outline-none dark:border-indigo-500 dark:bg-gray-800 dark:text-white"
      />
    );
  }

  return (
    <>
      <h1
        className="text-2xl font-semibold leading-7 text-gray-900 dark:text-white"
        data-testid="service-title"
      >
        {title}
      </h1>
      <button
        type="button"
        onClick={() => setIsEditing(true)}
        data-testid="service-title-edit"
        title="Rename AI Service"
        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
      >
        <PencilIcon className="h-4 w-4" />
        <span className="sr-only">Rename AI Service</span>
      </button>
    </>
  );
}

export default ServiceTitleEditor;
